/**
 * 加载遮罩组件
 * 用于显示全局加载状态
 */

import React from 'react';
import { useGlobalState } from '../../viewmodels/appviewmodel.jsx';

/**
 * 加载动画组件
 * @param {Object} props - 组件属性
 * @param {string} props.size - 加载动画大小
 * @param {string} props.color - 加载动画颜色
 */
function LoadingSpinner({ size = 'medium', color = 'primary' }) {
  /**
   * 获取尺寸样式类
   * @param {string} size - 尺寸
   * @returns {string} 尺寸样式类
   */
  const getSizeClasses = (size) => {
    const sizes = {
      small: 'w-4 h-4',
      medium: 'w-8 h-8',
      large: 'w-12 h-12',
      xlarge: 'w-16 h-16'
    };
    
    return sizes[size] || sizes.medium;
  };
  
  /**
   * 获取颜色样式类
   * @param {string} color - 颜色
   * @returns {string} 颜色样式类
   */
  const getColorClasses = (color) => {
    const colors = {
      primary: 'text-primary',
      secondary: 'text-secondary',
      white: 'text-white',
      gray: 'text-gray-500'
    };
    
    return colors[color] || colors.primary;
  };
  
  return (
    <div className={`${getSizeClasses(size)} ${getColorClasses(color)} animate-spin`}>
      <svg className="w-full h-full" fill="none" viewBox="0 0 24 24">
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
        />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        />
      </svg>
    </div>
  );
}

/**
 * 加载遮罩组件
 * @param {Object} props - 组件属性
 * @param {boolean} props.show - 是否显示遮罩
 * @param {string} props.message - 加载消息
 * @param {string} props.variant - 遮罩变体
 * @param {string} props.className - 自定义样式类
 */
function LoadingOverlay({ 
  show, 
  message = '加载中...', 
  variant = 'full', 
  className = '' 
}) {
  const { isLoading } = useGlobalState();
  
  // 如果没有显式传入show属性，则使用全局加载状态
  const shouldShow = show !== undefined ? show : isLoading;
  
  if (!shouldShow) {
    return null;
  }
  
  /**
   * 获取变体样式类
   * @param {string} variant - 变体
   * @returns {string} 变体样式类
   */
  const getVariantClasses = (variant) => {
    const variants = {
      full: 'fixed inset-0 z-50 bg-background/80 backdrop-blur-sm',
      modal: 'absolute inset-0 z-40 bg-background/60 backdrop-blur-sm',
      inline: 'relative bg-background/40 backdrop-blur-sm rounded-lg'
    };
    
    return variants[variant] || variants.full;
  };
  
  return (
    <div className={`${getVariantClasses(variant)} ${className}`}>
      <div className="flex items-center justify-center min-h-full p-4">
        <div className="flex flex-col items-center space-y-4">
          {/* 加载动画 */}
          <LoadingSpinner 
            size={variant === 'inline' ? 'medium' : 'large'} 
            color={variant === 'full' ? 'primary' : 'gray'}
          />
          
          {/* 加载消息 */}
          {message && (
            <div className="text-center">
              <p className="text-sm font-medium text-foreground">
                {message}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * 内联加载组件
 * @param {Object} props - 组件属性
 * @param {boolean} props.loading - 是否加载中
 * @param {string} props.message - 加载消息
 * @param {React.ReactNode} props.children - 子组件
 * @param {string} props.className - 自定义样式类
 */
function InlineLoading({ 
  loading, 
  message = '加载中...', 
  children, 
  className = '' 
}) {
  if (loading) {
    return (
      <div className={`flex items-center justify-center p-8 ${className}`}>
        <div className="flex flex-col items-center space-y-3">
          <LoadingSpinner size="medium" color="primary" />
          {message && (
            <p className="text-sm text-muted-foreground">
              {message}
            </p>
          )}
        </div>
      </div>
    );
  }
  
  return children;
}

/**
 * 按钮加载组件
 * @param {Object} props - 组件属性
 * @param {boolean} props.loading - 是否加载中
 * @param {React.ReactNode} props.children - 子组件
 * @param {string} props.loadingText - 加载文本
 * @param {string} props.className - 自定义样式类
 */
function ButtonLoading({ 
  loading, 
  children, 
  loadingText = '加载中...', 
  className = '',
  ...props 
}) {
  return (
    <button 
      className={`flex items-center justify-center space-x-2 ${className}`}
      disabled={loading}
      {...props}
    >
      {loading && (
        <LoadingSpinner size="small" color="white" />
      )}
      <span>
        {loading ? loadingText : children}
      </span>
    </button>
  );
}

export default LoadingOverlay;
export { LoadingSpinner, InlineLoading, ButtonLoading };